import React, { useState, useRef, useEffect } from "react";
import { Row, Col, Button, Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import { getOrdersDetailsAPI, deliveredOrderVerifyAPI } from "../../api/pahrmacy";
import { toast } from "react-toastify";

const STATIC_ORDER_ID = "6c0f5eb2-4287-4931-a45e-38c16e826049";
const userId = "c02e8017-a748-433f-9f2f-89b4159580e6";

const DeliveryOrderPage = () => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const inputsRef = useRef([]);

    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal state
    const [modalType, setModalType] = useState(null); // "couldntVerify" | "reportIssue" | null
    const [remark, setRemark] = useState("");

    useEffect(() => {
        fetchOrderDetails();
    }, []);

    const fetchOrderDetails = async () => {
        try {
            setLoading(true);
            const res = await getOrdersDetailsAPI({ orderId: STATIC_ORDER_ID, userId });

            if (res?.data?.status === "expired") {
                setError(res.data.message || "Session expired");
                return;
            }

            if (res?.data?.status === true) {
                setOrderData(res.data.data || null);
            } else {
                setError(res?.data?.message || "Failed to fetch order details");
            }
        } catch (err) {
            console.error("Error fetching order details:", err);
            setError("Something went wrong while fetching order details");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (value, index) => {
        if (!/^[0-9]?$/.test(value)) return;
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        if (value && index < 5) {
            inputsRef.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputsRef.current[index - 1].focus();
        }
    };

    if (loading) {
        return (
            <div className="container-fluid mt-5 text-center">
                <Spinner color="primary" />
                <p className="mt-2">Loading order details...</p>
            </div>
        );
    }

    if (error || !orderData) {
        return (
            <div className="container-fluid mt-5 text-center">
                <p className="text-danger">{error || "No order data found"}</p>
            </div>
        );
    }

    const { order, orderItems } = orderData;
    const orderStatus = order?.status || order?.order_status || "unknown";
    const canVerify =
        orderStatus.toString().toLowerCase() !== "delivered" &&
        orderStatus.toString().toLowerCase() !== "cancelled";

    const openModal = (type) => {
        setRemark("");
        setModalType(type);
    };

    const closeModal = () => {
        setModalType(null);
        setRemark("");
    };

    const handleMarkDelivered = async () => {
        if (!order?.id) return;
        const enteredOtp = otp.join("");
        if (enteredOtp.length !== 6) {
            toast.error("Please enter valid 6-digit OTP");
            return;
        }
        try {
            const payload = { orderId: order.id, otp: enteredOtp };
            const res = await deliveredOrderVerifyAPI(payload);
            if (res?.data?.status === true) {
                toast.success(res.data.message || "Order marked delivered");
                setOtp(["", "", "", "", "", ""]);
                fetchOrderDetails();
            } else if (res?.data?.status === "expired") {
                toast.error(res.data.message || "Session expired");
            } else {
                toast.error(res?.data?.message || "Invalid OTP");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to update order status");
        }
    };

    const handleModalSubmit = async () => {
        if (!order?.id) return;
        if (!remark.trim()) {
            toast.error("Please enter a reason");
            return;
        }
        try {
            const status = modalType === "couldntVerify" ? "verification_failed" : "issue_reported";
            const payload = { orderId: order.id, status, remark };
            const res = await deliveredOrderVerifyAPI(payload);
            if (res?.data?.status === true) {
                toast.success(res.data.message || "Submitted successfully");
                closeModal();
                fetchOrderDetails();
            } else {
                toast.error(res?.data?.message || "Failed to submit");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to submit");
        }
    };

    const itemTotal =
        orderItems?.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0) || 0;
    const discount = Number(order?.discountAmount) || 0;
    const totalAmount = Number(order?.totalAmount) || 0;
    const deliveryCharges = 40;
    const packagingCharges = 10;

    return (
        <div className="container mt-3">
            <Row className="g-3 mt-3">
                {/* ORDER NUMBER */}
                <Col xs={12}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 text-primary">Order #{order?.orderNum}</h5>
                    </div>
                </Col>

                {/* USER DETAILS */}
                <Col xs={12} sm={12} md={6}>
                    <div className="card h-100 p-2 p-md-3">
                        <div className="card-inner">
                            <h6 className="title text-primary">User Details</h6>
                            <hr className="my-3" style={{ opacity: 0.3 }} />
                            <div className="mt-3">
                                <p><strong>Customer Name:</strong> {order?.address?.name || "N/A"}</p>
                                <p><strong>Phone:</strong> {order?.address?.mobile}</p>
                                <p><strong>Email:</strong> {order?.address?.email || "N/A"}</p>
                                <p><strong>City:</strong> {order?.address?.city || "N/A"}</p>
                            </div>
                        </div>
                    </div>
                </Col>

                {/* DELIVERY INFORMATION */}
                <Col xs={12} sm={12} md={6}>
                    <div className="card h-100 p-2 p-md-3">
                        <div className="card-inner">
                            <h6 className="title text-primary">Delivery Information</h6>
                            <hr className="my-3" style={{ opacity: 0.3 }} />
                            <div className="mt-3">
                                <p>{order?.address?.name || "N/A"}</p>
                                <p>{order?.address?.addressLine1}</p>
                                <p>{order?.address?.city}</p>
                                <p>{order?.address?.state} - {order?.address?.postalCode}</p>
                            </div>
                        </div>
                    </div>
                </Col>

                {/* PAYMENT SUMMARY */}
                <Col xs={12} sm={12} md={6}>
                    <div className="card">
                        <div className="card-inner">
                            <h6 className="title text-primary">Order Payment Summary</h6>
                            <hr className="my-3" style={{ opacity: 0.3 }} />
                            <div className="d-flex justify-content-between mb-1">
                                <span>Item Total</span>
                                <span>₹{itemTotal.toFixed(2)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                                <span>Delivery Charges</span>
                                <span>₹{deliveryCharges}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                                <span>Packaging Charges</span>
                                <span>₹{packagingCharges}</span>
                            </div>
                            <div className="d-flex justify-content-between text-primary mb-1">
                                <span>Discount</span>
                                <span>-₹{discount.toFixed(2)}</span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between fw-bold">
                                <span>Total Amount</span>
                                <span>₹{totalAmount.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                    <div
                        className="alert alert-info d-flex align-items-start mt-3"
                        style={{ borderRadius: "10px", backgroundColor: "#e7f3ff" }}
                    >
                        <div className="me-2 fs-5">ℹ️</div>
                        <div>
                            <h6 className="mb-1 fw-bold text-primary">Important Note</h6>
                            <p className="mb-0 text-muted">
                                Please verify the OTP with customer before completing the delivery.
                            </p>
                        </div>
                    </div>
                </Col>

                {/* OTP VERIFY */}
                <Col xs={12} sm={12} md={6}>
                    <div className="card h-100 p-2 p-md-3">
                        <div className="card-inner">
                            <h6 className="title text-primary">Verify Order with OTP</h6>
                            <hr className="my-3" style={{ opacity: 0.3 }} />
                            <div className="alert alert-primary mt-2">
                                Please ask customer to share OTP sent to their mobile number
                            </div>
                            <h6 className="title">Enter OTP</h6>
                            <div className="d-flex flex-wrap justify-content-center gap-2 my-4">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputsRef.current[index] = el)}
                                        type="text"
                                        maxLength="1"
                                        value={digit}
                                        onChange={(e) => handleChange(e.target.value, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        className="form-control text-center"
                                        style={{
                                            width: "clamp(40px, 10vw, 55px)",
                                            height: "clamp(40px, 10vw, 55px)",
                                            fontSize: "clamp(14px, 3vw, 18px)"
                                        }}
                                    />
                                ))}
                            </div>
                            <p className="mb-2" style={{ paddingTop: "5.00rem" }}>
                                OTP Sent to: <strong>{order?.address?.mobile}</strong>
                            </p>
                            <hr className="my-3" style={{ opacity: 0.3 }} />

                            <Button color="success" className="w-100 mb-2 py-2" onClick={handleMarkDelivered} disabled={!canVerify}>
                                {orderStatus.toString().toLowerCase() === "delivered" ? "Delivered" : "✓  Verify & Complete Delivery"}
                            </Button>

                            <Button color="light" className="w-100 mb-2 py-2" onClick={() => openModal("couldntVerify")}>
                                Couldn't Verify OTP?
                            </Button>

                            <Button color="danger" outline className="w-100 mb-2 py-2" onClick={() => openModal("reportIssue")}>
                                Report an Issue
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* MODAL */}
            <Modal isOpen={!!modalType} toggle={closeModal} centered>
                <ModalHeader toggle={closeModal}>
                    {modalType === "couldntVerify" ? "Couldn't Verify OTP" : "Report an Issue"}
                </ModalHeader>
                <ModalBody>
                    {/* TODO: paste the exact text you want shown here */}
                    <p>Report the problem you are facing</p>
                    <Input
                        type="textarea"
                        placeholder="Enter reason / remark"
                        value={remark}
                        onChange={(e) => setRemark(e.target.value)}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={closeModal}>Cancel</Button>
                    <Button color="primary" onClick={handleModalSubmit}>Submit</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default DeliveryOrderPage;